import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/adminService';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';

import WebhookEventsPanel from './components/WebhookEventsPanel';
import EdgeFunctionLogsPanel from './components/EdgeFunctionLogsPanel';
import JwtFailuresPanel from './components/JwtFailuresPanel';
import StatsOverview from './components/StatsOverview';

const AdminDashboard = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState({
    totalWebhookEvents: 0,
    totalLogs: 0,
    totalJwtFailures: 0
  });

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/agent-dashboard' },
    { label: 'Admin Monitoring', path: '/admin-dashboard' }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'webhooks', label: 'Stripe Webhooks', icon: 'Webhook' },
    { id: 'logs', label: 'Edge Function Logs', icon: 'FileText' },
    { id: 'jwt', label: 'JWT Failures', icon: 'ShieldAlert' },
    { id: 'payments', label: 'Payment Monitoring', icon: 'CreditCard' }
  ];

  useEffect(() => {
    checkAdminAccess();
  }, [user]);

  useEffect(() => {
    if (isAdmin) {
      loadStats();
    }
  }, [isAdmin]);

  const checkAdminAccess = async () => {
    setLoading(true);
    try {
      const adminStatus = await adminService?.isAdmin();
      setIsAdmin(adminStatus);
      
      if (!adminStatus) {
        // Redirect non-admin users
        navigate('/agent-dashboard');
      }
    } catch (error) {
      console.error('Admin check error:', error);
      navigate('/agent-dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await adminService?.getMonitoringStats();
      setStats(statsData);
    } catch (error) {
      console.error('Load stats error:', error);
    }
  };

  const handleRefresh = () => {
    loadStats();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Admin Dashboard - OfferSense</title>
        </Helmet>
        <Header />
        <main className="container mx-auto px-4 py-6 md:py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="h-32 bg-muted rounded"></div>
              <div className="h-32 bg-muted rounded"></div>
              <div className="h-32 bg-muted rounded"></div>
            </div>
            <div className="h-96 bg-muted rounded"></div>
          </div>
        </main>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Admin Dashboard - OfferSense</title>
        <meta name="description" content="Monitor Stripe webhooks, Edge Function logs, and JWT validation failures" />
      </Helmet>

      <Header />

      <main className="container mx-auto px-4 py-6 md:py-8">
        <Breadcrumbs items={breadcrumbItems} />

        <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
              Admin Monitoring Dashboard
            </h1>
            <p className="text-muted-foreground text-sm md:text-base">
              Real-time monitoring for payment troubleshooting and system health
            </p>
          </div>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Icon name="RefreshCw" size={16} />
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border mb-6">
          <div className="flex gap-1 overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'overview' && (
            <>
              <StatsOverview stats={stats} />
              <div className="grid grid-cols-1 gap-6">
                <WebhookEventsPanel compact limit={5} />
                <EdgeFunctionLogsPanel compact limit={5} />
                <JwtFailuresPanel compact limit={5} />
              </div>
            </>
          )}

          {activeTab === 'webhooks' && <WebhookEventsPanel />}

          {activeTab === 'logs' && <EdgeFunctionLogsPanel />}

          {activeTab === 'jwt' && <JwtFailuresPanel />}

          {activeTab === 'payments' && (
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="CreditCard" size={20} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Payment Monitoring</h2>
                  <p className="text-sm text-muted-foreground">Comprehensive payment troubleshooting dashboard</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/payment-monitoring-dashboard')}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Icon name="ExternalLink" size={16} />
                Open Payment Monitoring Dashboard
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;